import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, Trash2, ShieldCheck, FileText, LifeBuoy } from "lucide-react";
import Footer from "@/Footer";
import { DeletionRequestForm } from "@/Forms";

const SUPPORT_EMAIL = "support@herrellinteractive.com";
const COMPANY = "Herrell Homestead LLC";
const EFFECTIVE = "June 2026";

const Shell = ({ icon, title, subtitle, children }) => (
  <div className="hi-bg hi-grain min-h-screen">
    <div className="relative z-[2] max-w-3xl mx-auto px-5 py-8 md:py-12">
      <Link
        to="/"
        data-testid="legal-back"
        className="hi-cta inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold hi-body bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 hover:text-white"
      >
        <ArrowLeft size={16} strokeWidth={2.5} /> Back to Suite
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="mt-8"
      >
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#3b9bff]/15 text-[#3b9bff] border border-[#3b9bff]/30">
            {icon}
          </span>
          <h1 className="hi-serif text-3xl md:text-4xl text-[#f3ead4]">{title}</h1>
        </div>
        {subtitle && <p className="hi-body text-white/55 mt-3">{subtitle}</p>}
        <p className="hi-body text-xs text-white/40 mt-2">Effective {EFFECTIVE}</p>

        <div className="hi-hairline w-full mt-6 mb-8" />

        <div className="hi-body text-white/70 leading-relaxed space-y-6 legal-body">
          {children}
        </div>
      </motion.div>
    </div>
    <div className="relative z-[2]">
      <Footer />
    </div>
  </div>
);

const H = ({ children }) => (
  <h2 className="hi-serif text-xl text-[#f3ead4] mb-2">{children}</h2>
);
const P = ({ children }) => <p className="text-[15px]">{children}</p>;
const UL = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1.5 text-[15px] text-white/65">
    {items.map((t, i) => (
      <li key={i}>{t}</li>
    ))}
  </ul>
);

export const Privacy = () => (
  <Shell
    icon={<ShieldCheck size={20} />}
    title="Privacy Policy"
    subtitle={`How ${COMPANY} handles your information across the Herrell Interactive suite of apps.`}
  >
    <section><P>This Privacy Policy applies to Herrell Interactive and all apps published by {COMPANY} (including ApiaryKeeper™, MileageKeeper™, EvidenceKeeper™, GreenKeep™, FlockKeeper™ and related apps, collectively the “Apps”). We are committed to protecting your privacy and being transparent about the data we collect.</P></section>

    <section><H>Information We Collect</H>
      <UL items={[
        "Account information you provide (such as name and email address) when you create an account.",
        "Content you add to an app (for example hive records, mileage logs, documents, photos, garden plans or flock records).",
        "Device and usage information (such as device type, app version, and crash/diagnostic data) used to keep the Apps reliable.",
      ]} />
    </section>

    <section><H>How We Use Your Information</H>
      <UL items={[
        "To provide, maintain and improve the Apps and their features.",
        "To sync and back up your data across your devices.",
        "To provide AI-assisted features you request (for example vision diagnosis or receipt scanning).",
        "To respond to support requests and send important service notices.",
      ]} />
    </section>

    <section><H>Data Sharing</H>
      <P>We do not sell your personal information. We share data only with service providers that help us operate the Apps (such as secure cloud hosting and AI processing), and only to the extent needed to provide the service, or when required by law.</P>
    </section>

    <section><H>Data Security & Retention</H>
      <P>We use industry-standard safeguards, including encryption in transit and at rest where applicable. We retain your data for as long as your account is active. You may request deletion at any time — see our <Link to="/delete-account" className="text-[#3b9bff] underline">Account & Data Deletion</Link> page.</P>
    </section>

    <section><H>Children’s Privacy</H>
      <P>The Apps are not directed to children under 13, and we do not knowingly collect personal information from children under 13.</P>
    </section>

    <section><H>Your Rights</H>
      <P>Depending on your location, you may have the right to access, correct, export or delete your personal data. To exercise these rights, contact us at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#3b9bff] underline">{SUPPORT_EMAIL}</a>.</P>
    </section>

    <section><H>Contact Us</H>
      <P>{COMPANY} — <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#3b9bff] underline">{SUPPORT_EMAIL}</a></P>
    </section>
  </Shell>
);

export const Terms = () => (
  <Shell
    icon={<FileText size={20} />}
    title="Terms & Conditions"
    subtitle={`The terms that govern your use of the Herrell Interactive apps by ${COMPANY}.`}
  >
    <section><P>By downloading, accessing or using any app published by {COMPANY} (the “Apps”), you agree to these Terms & Conditions. If you do not agree, please do not use the Apps.</P></section>

    <section><H>License</H>
      <P>We grant you a personal, non-exclusive, non-transferable, revocable license to use the Apps for your own lawful purposes, subject to these Terms.</P>
    </section>

    <section><H>Acceptable Use</H>
      <UL items={[
        "Do not use the Apps for any unlawful purpose or in violation of any applicable law.",
        "Do not attempt to reverse engineer, disrupt, or gain unauthorized access to the Apps or their systems.",
        "You are responsible for the accuracy of the content and records you enter.",
      ]} />
    </section>

    <section><H>Beta Software</H>
      <P>Some Apps or features are offered as beta releases and may contain bugs or change without notice. Beta features are provided “as is” and should not be solely relied upon for critical decisions.</P>
    </section>

    <section><H>Disclaimers</H>
      <P>The Apps are provided “as is” and “as available” without warranties of any kind. Information provided by AI-assisted features is for guidance only and is not professional, legal, veterinary, financial or agricultural advice.</P>
    </section>

    <section><H>Limitation of Liability</H>
      <P>To the maximum extent permitted by law, {COMPANY} shall not be liable for any indirect, incidental or consequential damages arising from your use of the Apps.</P>
    </section>

    <section><H>Changes</H>
      <P>We may update these Terms from time to time. Continued use of the Apps after changes take effect constitutes acceptance of the revised Terms.</P>
    </section>

    <section><H>Contact</H>
      <P>Questions about these Terms? Email <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#3b9bff] underline">{SUPPORT_EMAIL}</a>.</P>
    </section>
  </Shell>
);

export const Support = () => (
  <Shell
    icon={<LifeBuoy size={20} />}
    title="Support"
    subtitle="We’re here to help. Reach out and we’ll get back to you as soon as we can."
  >
    <section className="not-prose">
      <a
        href={`mailto:${SUPPORT_EMAIL}`}
        data-testid="support-email-btn"
        className="hi-cta inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold hi-body bg-[#c9a24b] text-[#1a2417] hover:bg-[#e0bb63]"
      >
        <Mail size={17} strokeWidth={2.5} /> Email Support
      </a>
    </section>

    <section><H>Contact</H>
      <P>Email us at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#3b9bff] underline">{SUPPORT_EMAIL}</a>. We typically respond within 1–2 business days.</P>
    </section>

    <section><H>Frequently Asked</H>
      <UL items={[
        "Which apps are available now? ApiaryKeeper™, MileageKeeper™ and EvidenceKeeper™ are live (in beta). Others are coming soon.",
        "Are the live apps final? They are currently in beta testing and improving quickly — your feedback helps shape them.",
        "How do I delete my account or data? Visit our Account & Data Deletion page for step-by-step instructions.",
        "Do you sell my data? No. See our Privacy Policy for details.",
      ]} />
    </section>

    <section><H>Legal</H>
      <P>See our <Link to="/privacy" className="text-[#3b9bff] underline">Privacy Policy</Link> and <Link to="/terms" className="text-[#3b9bff] underline">Terms & Conditions</Link>.</P>
    </section>
  </Shell>
);

export const DeleteAccount = () => (
  <Shell
    icon={<Trash2 size={20} />}
    title="Account & Data Deletion"
    subtitle={`How to delete your account and associated data from ${COMPANY} apps.`}
  >
    <section><P>You can request deletion of your account and personal data at any time. This page explains what gets deleted, what may be retained, and how to make a request — as required by the Google Play and Apple App Store policies.</P></section>

    <section><H>Delete From Within the App</H>
      <UL items={[
        "Open the app and go to Settings → Account.",
        "Tap “Delete Account”.",
        "Confirm the request. Your account and associated data will be scheduled for deletion.",
      ]} />
    </section>

    <section><H>Request Deletion Online</H>
      <P>Submit a deletion request directly using the form below and we'll take care of the rest.</P>
      <div className="mt-5">
        <DeletionRequestForm />
      </div>
    </section>

    <section><H>Prefer Email?</H>
      <P>You can also email <a href={`mailto:${SUPPORT_EMAIL}?subject=Account%20and%20Data%20Deletion%20Request`} className="text-[#3b9bff] underline">{SUPPORT_EMAIL}</a> from the address associated with your account with the subject “Account and Data Deletion Request”, and tell us which app(s) you use. We will verify your identity and process the request.</P>
    </section>

    <section><H>What Is Deleted</H>
      <UL items={[
        "Your account profile and login credentials.",
        "Content you created in the app (records, logs, documents, photos and related data).",
        "Personal identifiers associated with your account.",
      ]} />
    </section>

    <section><H>What May Be Retained</H>
      <P>We may retain limited information where required by law, for security and fraud prevention, or in anonymized/aggregated form that no longer identifies you. Backups are purged on a rolling schedule.</P>
    </section>

    <section><H>Timeline</H>
      <P>Deletion requests are typically completed within 30 days of verification. You will receive confirmation once your data has been removed.</P>
    </section>

    <section><H>Contact</H>
      <P>{COMPANY} — <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#3b9bff] underline">{SUPPORT_EMAIL}</a></P>
    </section>
  </Shell>
);
